import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { checkEmailFormat } from '../../utils/validations';
import { accountAPI } from '../../services/redwolfSdkAPI';
import { checkEmailIdExits, getUserConsentStatus } from '../../utils/accessRedirection';

import { useGoogleAnalytics } from '../common/hooks/useGoogleAnalytics';
import { useRecaptcha } from '../common/hooks/useRecaptcha';
import useHeadingFocuser from '../common/hooks/useHeadingFocuser';
import useTechnicalErrorMessage from '../common/hooks/useTechnicalErrorMessage';

import { addClasses } from '../../utils/eventListeners';
import { setTermsOfServiceStatus, setUserProfile, UserProfile } from '../../store/userSlice';
import { useDispatch } from 'react-redux';

import SignInGoogle from './SignInGoogle';
import FacebookSignIn from './SignInFacebook';
import SignInApple from './SignInApple';
import Spinner from '../common/components/Spinner';

import * as Sentry from '@sentry/react';
import { SocialResponse } from '../common/interfaces/user';
import { openNotification } from '../../store/commonSlice';
import { notification } from '../../utils/constants';
import useRequestRedwolfService from '../common/hooks/useRequestRedwolfService';
import { setToken, setLocalStorage } from '../../utils/localStorage';
import { appConfig } from '../../utils/appConfig';

import vizioLogo from '../../images/vizio-account-logo.png';
import iconvizio from '../../images/icon-vizio.svg';
import icontick from '../../images/icon-tick.svg';
import iconstar from '../../images/icon-star.svg';
import iconcalendar from '../../images/icon-calendar.svg';
import iconwatsup from '../../images/icon-watsup.svg';
import iconmultipletv from '../../images/icon-multiple-tv.svg';
import useRedirection from '../common/hooks/useRedirection';

const SignIn = () => {
	const [signInForm, setSignInForm] = useState({ email: '', password: '' });
	const [formErrors, setFormErrorMessage] = useState({
		isEmailFieldValid: true,
		isEmailValidFormt: true,
		emailErrorMessage: '',
		isPasswordFieldValid: true,
		passwordErrorMessage: ''
	});
	const [isSignButtonDisabled, setIsSignButtonDisabled] = useState(false);
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [isUnableFindEmail, setIsUnableFindEmail] = useState(false);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(true);
	const [isAccountLocked, setIsAccountLocked] = useState(false);
	const [isEmailChanged, setIsEmailChanged] = useState(true);
	const [isRecaptchaLoading, generateRecaptchaToken]: any = useRecaptcha();

	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();
	const [userRedirection] = useRedirection();
	const [headingRef] = useHeadingFocuser();

	const emailRef = useRef<HTMLInputElement>(null);
	const passwordRef = useRef<HTMLInputElement>(null);

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const queryStrings = new URLSearchParams(useLocation().search);
	const redirectURLFromQueryString = queryStrings.get('redirect_url');

	const transaction = Sentry.startInactiveSpan({ name: 'Sign In' });

	useEffect(() => {
		addClasses();
		document.body.classList.add('login_page');
		return () => {
			document.body.classList.remove('login_page');
		};
	}, []);

	useEffect(() => {
		setIsSpinnerLoading(!isRecaptchaLoading);
	}, [isRecaptchaLoading]);

	const handleChange = (event: any) => {
		const { name, value } = event.target;

		setSignInForm({ ...signInForm, [name]: value });

		if (name === 'email') {
			formErrors.emailErrorMessage = '';
			formErrors.isEmailFieldValid = true;
			formErrors.isEmailValidFormt = true;
			setIsUnableFindEmail(false);
			setIsEmailChanged(true);
		}
		if (name === 'password') {
			formErrors.passwordErrorMessage = '';
			formErrors.isPasswordFieldValid = true;
			setIsAccountLocked(false);
		}

		setFormErrorMessage(formErrors);

		if (formErrors.isPasswordFieldValid && formErrors.isEmailFieldValid) {
			setIsSignButtonDisabled(false);
		} else {
			setIsSignButtonDisabled(true);
		}
	};

	const handleEmailBlur = () => {
		if (signInForm.email && !checkEmailFormat(signInForm.email)) {
			setFormErrorMessage({
				...formErrors,
				isEmailValidFormt: false,
				isEmailFieldValid: false,
				emailErrorMessage: 'Please enter a valid email address.'
			});
			setIsSignButtonDisabled(true);
		} else if (signInForm.email && checkEmailFormat(signInForm.email)) {
			setFormErrorMessage({
				...formErrors,
				isEmailValidFormt: true,
				isEmailFieldValid: true,
				emailErrorMessage: ''
			});
		}
	};

	const handlePasswordBlur = () => {
		setIsAccountLocked(false);
		if (signInForm.password) {
			setFormErrorMessage({ ...formErrors, isPasswordFieldValid: true, passwordErrorMessage: '' });
			setIsSignButtonDisabled(false);
		}
	};

	const passwordVisibleHide = () => {
		setIsPasswordVisible(!isPasswordVisible);
	};

	const signInSubmit = (event: any) => {
		event.preventDefault();

		if (passwordRef.current) {
			passwordRef.current.blur();
		}

		if (!signInForm.email) {
			formErrors.isEmailFieldValid = false;
			formErrors.emailErrorMessage = 'This is a required field.';
			if (emailRef.current !== null) {
				emailRef.current.focus();
			}
		}
		if (!signInForm.password) {
			formErrors.isPasswordFieldValid = false;
			formErrors.passwordErrorMessage = 'This is a required field.';
			if (passwordRef.current !== null && formErrors.emailErrorMessage === '') {
				passwordRef.current.focus();
			}
		}
		if (signInForm.email && !checkEmailFormat(signInForm.email)) {
			formErrors.isEmailFieldValid = false;
			formErrors.emailErrorMessage = 'Please enter a valid email address.';
			if (
				emailRef.current !== null &&
				formErrors.emailErrorMessage === '' &&
				formErrors.passwordErrorMessage === ''
			) {
				emailRef.current.focus();
			}
		}
		setFormErrorMessage(formErrors);

		if (formErrors.isEmailFieldValid && formErrors.isPasswordFieldValid) {
			if (isEmailChanged) {
				setIsEmailChanged(false);
				checkEmailExits(signInForm.email);
			} else {
				setIsSpinnerLoading(true);
				callLoginAPI(signInForm.email, signInForm.password, 'password');
			}
		} else {
			setIsSignButtonDisabled(true);
		}
	};

	const checkEmailExits = async (email: any) => {
		setIsSpinnerLoading(true);

		const captchaToken = await generateRecaptchaToken('vz_account_check_email_exits').then(
			(response: any) => response
		);

		checkEmailIdExits(email, captchaToken)
			.then((isEmailExists: any) => {
				if (isEmailExists) {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_EMAIL_CHECK', { item_title: 'valid' });

					callLoginAPI(signInForm.email, signInForm.password, 'password');
				} else {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_EMAIL_CHECK', { item_title: 'invalid' });

					transaction && transaction.end();
					setIsSpinnerLoading(false);
					setIsUnableFindEmail(true);
					setIsSignButtonDisabled(true);
					if (emailRef.current) {
						emailRef.current.focus();
					}
				}
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const socialSignIn = (socialRespose: SocialResponse) => {
		setIsSpinnerLoading(true);
		callSocialLoginAPI(socialRespose.email, socialRespose.token, socialRespose.type, socialRespose);
	};

	const callLoginAPI = async (
		email: string,
		credentials: string,
		signInType: string,
		socialResponse?: SocialResponse
	) => {
		const captchaToken = await generateRecaptchaToken('vz_account_traditional_signin').then(
			(response: any) => response
		);

		const loginParamObject = {
			email: email,
			password: credentials,
			reCaptchaToken: captchaToken
		};
		requestRedwolfService(accountAPI.login(loginParamObject))
			.then((response: any) => {
				if (response.constructor.$metadata$.simpleName === 'Success' && response.data) {
					// TODO:(4) Make this GA name logic re-usable
					let gaEvent = '';
					if (signInType === 'password') {
						gaEvent = 'Vizio_Account_web_Traditional_SignIn';
					} else if (signInType === 'facebook') {
						gaEvent = 'Vizio_Account_web_FB_SignIn';
					} else if (signInType === 'google') {
						gaEvent = 'Vizio_Account_web_Google_SignIn';
					} else if (signInType === 'apple') {
						gaEvent = 'Vizio_Account_web_Apple_SignIn';
					}

					logGoogleAnalyticsEvent(gaEvent, {
						item_title: signInType === 'password' ? 'Traditional' : signInType
					});

					const responseData = { ...response.data };
					//TODO:(1) Review requiresCreateUser - Its not part of the accountAPI.login

					const userProfile: UserProfile = {
						firstName: responseData.user.firstName,
						lastName: responseData.user.lastName,
						email: responseData.user.email,
						userId: responseData.user.userId,
						signInType: signInType === 'password' ? 'TRADITIONAL' : signInType,
						requiresCreateUser: responseData.requiresCreateUser,
						isUserLoggedIn: true,
						emailVerified: responseData.user.emailVerified,
						requiresEmailVerification: responseData.user.requiresEmailVerification,
						requiresPersonalization: responseData.user.requiresPersonalization
					};

					setLocalStorage('requiresEmailVerification', responseData.user.requiresEmailVerification);
					if (!responseData.hasPasswordStrong && signInType === 'password') {
						setToken(responseData.token);
						dispatch(setUserProfile({ userProfile, user: responseData.user }));
						setLocalStorage('isRequiresPersonalization', responseData.user.requiresPersonalization);

						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_PASSWORD_CHECK', {
							item_title: responseData.hasPasswordStrong ? 'secure' : 'insecure'
						});
						setLocalStorage('isPasswordStrong', responseData.hasPasswordStrong);

						getUserConsentStatus(appConfig.redirectConsentIds).then((acceptedTerms: any) => {
							setIsSpinnerLoading(false);

							dispatch(setTermsOfServiceStatus(acceptedTerms));

							if (acceptedTerms === 'ACTIVE') {
								dispatch(
									openNotification({
										type: 'SUCCESS',
										heading: 'Success',
										body: notification.message.updatePasswordMsg
									})
								);
								navigate('/password-update');
								transaction && transaction.end();
							} else if (
								acceptedTerms === 'UNKNOWN' ||
								acceptedTerms === 'WITHDRAWN' ||
								acceptedTerms === 'POLICY_CHANGE' ||
								acceptedTerms === 'Failed'
							) {
								navigate(`/terms-of-service?${queryStrings.toString()}`);
							} else {
								showTechnicalErrorMessage();
							}
							transaction && transaction.end();
						});
					} else {
						setToken(responseData.token);
						dispatch(setUserProfile({ userProfile, user: responseData.user }));
						setLocalStorage('isRequiresPersonalization', responseData.user.requiresPersonalization);
						getUserConsentStatus(appConfig.redirectConsentIds).then((acceptedTerms: any) => {
							setIsSpinnerLoading(false);

							dispatch(setTermsOfServiceStatus(acceptedTerms));

							if (acceptedTerms === 'ACTIVE') {
								dispatch(
									openNotification({
										type: 'SUCCESS',
										heading: 'Success',
										body: notification.message.signInSuccess
									})
								);
								transaction && transaction.end();
								userRedirection(redirectURLFromQueryString);
							} else if (
								acceptedTerms === 'UNKNOWN' ||
								acceptedTerms === 'WITHDRAWN' ||
								acceptedTerms === 'POLICY_CHANGE' ||
								acceptedTerms === 'Failed'
							) {
								dispatch(
									openNotification({
										type: 'SUCCESS',
										heading: 'Success',
										body: notification.message.signInSuccess
									})
								);
								if (responseData.user.requiresEmailVerification) {
									navigate(`/confirm-email`);
								} else {
									navigate(`/terms-of-service?${queryStrings.toString()}`);
								}
							} else {
								showTechnicalErrorMessage();
							}
							transaction && transaction.end();
						});
					}
				} else {
					if (
						response.response &&
						(response.response.status === 400 || response.response.status === 401) &&
						signInType === 'password'
					) {
						if (response?.response?.detail === 'ACCOUNT_LOCKED') {
							setFormErrorMessage({
								...formErrors,
								isEmailFieldValid: false,
								isPasswordFieldValid: false,
								passwordErrorMessage:
									'Your account is locked for 5 minutes due to too many login attempts.'
							});
							setIsAccountLocked(true);
						} else {
							setFormErrorMessage({
								...formErrors,
								isEmailFieldValid: false,
								isPasswordFieldValid: false,
								passwordErrorMessage: 'Please enter a valid Email Address and Password'
							});
						}
						if (emailRef.current) {
							emailRef.current.focus();
						}
						setIsSignButtonDisabled(true);
					} else {
						if (response?.response?.detail === 'Unauthorized') {
							navigate('/sign-up');
						} else {
							if (response.response && response.response.status != 400) {
								Sentry.captureException(new Error('login:' + response));
							}
							showTechnicalErrorMessage();
						}
					}
					setIsSpinnerLoading(false);
				}
			})
			.catch((err: any) => {
				showTechnicalErrorMessage();
				transaction && transaction.end();
				setIsSpinnerLoading(false);
				Sentry.captureException(err);
			});
	};

	const callSocialLoginAPI = async (
		email: string,
		credentials: string,
		signInType: string,
		socialResponse?: SocialResponse
	) => {
		const captchaToken = await generateRecaptchaToken('vz_account_social_signin').then(
			(response: any) => response
		);

		let loginParamObject = {};
		// TODO:(5) Make this signInType block re-usable
		if (signInType === 'facebook') {
			loginParamObject = {
				email: email,
				facebookToken: credentials,
				googleToken: '',
				appleToken: '',
				reCaptchaToken: captchaToken
			};
		} else if (signInType === 'google') {
			loginParamObject = {
				email: email,
				facebookToken: '',
				googleToken: credentials,
				appleToken: '',
				reCaptchaToken: captchaToken
			};
		} else if (signInType === 'apple') {
			loginParamObject = {
				email: email,
				facebookToken: '',
				googleToken: '',
				appleToken: credentials,
				reCaptchaToken: captchaToken
			};
		}
		requestRedwolfService(accountAPI.socialLogin(loginParamObject))
			.then((response: any) => {
				if (response.constructor.$metadata$.simpleName === 'Success' && response.data) {
					// TODO:(4) Make this GA name logic re-usable
					let gaEvent = '';
					if (signInType === 'password') {
						gaEvent = 'Vizio_Account_web_Traditional_SignIn';
					} else if (signInType === 'facebook') {
						gaEvent = 'Vizio_Account_web_FB_SignIn';
					} else if (signInType === 'google') {
						gaEvent = 'Vizio_Account_web_Google_SignIn';
					} else if (signInType === 'apple') {
						gaEvent = 'Vizio_Account_web_Apple_SignIn';
					}

					logGoogleAnalyticsEvent(gaEvent, {
						item_title: signInType === 'password' ? 'Traditional' : signInType
					});

					const responseData = { ...response.data };
					//TODO:(1) Review requiresCreateUser - Its not part of the accountAPI.login

					const userProfile: UserProfile = {
						firstName: responseData.user.firstName,
						lastName: responseData.user.lastName,
						email: responseData.user.email,
						userId: responseData.user.userId,
						signInType: signInType === 'password' ? 'TRADITIONAL' : signInType,
						requiresCreateUser: responseData.requiresCreateUser,
						isUserLoggedIn: true,
						emailVerified: responseData.user.emailVerified,
						requiresEmailVerification: responseData.user.requiresEmailVerification,
						requiresPersonalization: responseData.user.requiresPersonalization
					};
					setLocalStorage('requiresEmailVerification', false);
					if (responseData.user.requiresPersonalization) {
						navigate(`/account?${queryStrings.toString()}`, {
							state: {
								socialResponse: socialResponse,
								isSocialLogin: true,
								token: responseData.token,
								requiresPersonalizationModalOpen: true
							}
						});
					} else if (!responseData.hasPasswordStrong && signInType === 'password') {
						setToken(responseData.token);
						dispatch(setUserProfile({ userProfile, user: responseData.user }));
						setLocalStorage('isRequiresPersonalization', responseData.user.requiresPersonalization);

						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_PASSWORD_CHECK', {
							item_title: responseData.hasPasswordStrong ? 'secure' : 'insecure'
						});
						setLocalStorage('isPasswordStrong', responseData.hasPasswordStrong);
						navigate('/password-update');
					} else {
						setToken(responseData.token);
						dispatch(setUserProfile({ userProfile, user: responseData.user }));
						setLocalStorage('isRequiresPersonalization', responseData.user.requiresPersonalization);

						getUserConsentStatus(appConfig.redirectConsentIds).then((acceptedTerms: any) => {
							setIsSpinnerLoading(false);
							dispatch(setTermsOfServiceStatus(acceptedTerms));

							if (acceptedTerms === 'ACTIVE') {
								dispatch(
									openNotification({
										type: 'SUCCESS',
										heading: 'Success',
										body: notification.message.signInSuccess
									})
								);
								transaction && transaction.end();
								userRedirection(redirectURLFromQueryString);
							} else if (
								acceptedTerms === 'UNKNOWN' ||
								acceptedTerms === 'WITHDRAWN' ||
								acceptedTerms === 'POLICY_CHANGE' ||
								acceptedTerms === 'Failed'
							) {
								dispatch(
									openNotification({
										type: 'SUCCESS',
										heading: 'Success',
										body: notification.message.signInSuccess
									})
								);

								navigate(`/terms-of-service?${queryStrings.toString()}`);
							} else {
								showTechnicalErrorMessage();
							}
							transaction && transaction.end();
						});
					}
				} else {
					if (
						response.response &&
						(response.response.status === 400 || response.response.status === 401) &&
						signInType === 'password'
					) {
						if (response?.response?.detail === 'ACCOUNT_LOCKED') {
							setFormErrorMessage({
								...formErrors,
								isEmailFieldValid: false,
								isPasswordFieldValid: false,
								passwordErrorMessage:
									'Your account is locked for 5 minutes due to too many login attempts.'
							});
							setIsAccountLocked(true);
						} else {
							setFormErrorMessage({
								...formErrors,
								isEmailFieldValid: false,
								isPasswordFieldValid: false,
								passwordErrorMessage: 'Please enter a valid Email Address and Password'
							});
						}

						if (emailRef.current) {
							emailRef.current.focus();
						}
						setIsSignButtonDisabled(true);
					} else {
						if (response?.response?.detail === 'Unauthorized') {
							navigate('/sign-up');
						} else {
							Sentry.captureException(new Error('login:' + response));
							showTechnicalErrorMessage();
						}
					}
					setIsSpinnerLoading(false);
				}
			})
			.catch((err: any) => {
				showTechnicalErrorMessage();
				transaction && transaction.end();
				setIsSpinnerLoading(false);
				Sentry.captureException(err);
			});
	};

	const passwordHideShow = (event: any) => {
		if (event.key === 'Enter') {
			setIsPasswordVisible(!isPasswordVisible);
		}
	};

	return (
		<>
			<div className="login">
				<div className="content-info">
					<div className="account-logo">
						<img src={vizioLogo} alt="account-logo" />
					</div>
					<h1 ref={headingRef} aria-live="polite">
						Save time. Stream more.
					</h1>
					<p>
						From instant access to the apps VIZIO offers, free TV with WatchFree+, and automatic
						updates for your (smarter) TV, this is entertainment made easy. VIZIO Account required
						for Smart TV functionality and product updates.
					</p>
				</div>

				<div className="login-content login-container">
					<div className="form-container">
						<form className="login-form" id="login-form">
							<ul className="form-list">
								<li className="fields">
									<div
										className={`field ${
											!formErrors.isEmailFieldValid || isUnableFindEmail ? 'has-error' : ''
										}`}>
										<div className="input-box form-group input-label">
											<input
												name="email"
												id="email"
												type="text"
												className="form-control input-text required-entry validate-email login-field-transparent"
												aria-required="true"
												aria-describedby="unableFindEmailError emailError passwordError"
												value={signInForm.email}
												onChange={handleChange}
												onBlur={handleEmailBlur}
												ref={emailRef}
												spellCheck="false"
											/>
											<label htmlFor="email" className="control-label">
												Email
											</label>

											<code id="emailError" aria-atomic="true" aria-live="assertive">
												{formErrors.emailErrorMessage}
											</code>

											<code className="error_msg" id="unableFindEmailError">
												{isUnableFindEmail && (
													<span aria-live="polite">
														Unable to find email.{' '}
														<Link
															to={{ pathname: '/sign-up', search: `?${queryStrings.toString()}` }}
															state={{ email: signInForm.email }}>
															Create Account here
														</Link>
													</span>
												)}
											</code>
										</div>
									</div>
								</li>

								<li className="fields">
									<div className={`field ${!formErrors.isPasswordFieldValid ? 'has-error' : ''}`}>
										<div
											className={
												isPasswordVisible
													? 'input-box form-group input-label pw-view'
													: 'input-box form-group input-label'
											}>
											<input
												name="password"
												className="form-control password input-text required-entry login-field-transparent"
												id="pass"
												aria-required="true"
												aria-invalid="false"
												aria-describedby="passwordError"
												value={signInForm.password}
												onChange={handleChange}
												onBlur={handlePasswordBlur}
												ref={passwordRef}
												type={isPasswordVisible ? 'text' : 'password'}
											/>
											<label htmlFor="pass" className="control-label">
												Password
											</label>

											<code
												id="passwordError"
												className={`error-red ${isAccountLocked ? 'error_msg' : ''}`}>
												{formErrors.passwordErrorMessage}
											</code>

											<div
												className="input-field-icon"
												tabIndex={0}
												role="button"
												id="passwordToggle"
												aria-label={isPasswordVisible ? 'hide password' : 'show password'}
												onClick={passwordVisibleHide}
												onKeyDown={(event) => passwordHideShow(event)}>
												<em aria-hidden="true" className="icon-eye eye-icon"></em>
											</div>
										</div>
									</div>
								</li>

								<li>
									<button
										type="submit"
										className="primary-button btn-login"
										id="send2"
										onClick={signInSubmit}
										disabled={isSignButtonDisabled}>
										Sign In
									</button>
								</li>
							</ul>
							<Link to="/password-reset" className="link-forgot-pass link">
								Forgot password?
							</Link>
						</form>
					</div>

					<div className="social-contianer">
						<div className="social-buttons clearfix">
							<div className="social-inner-container">
								<FacebookSignIn facebookSignIn={socialSignIn} />

								<SignInGoogle googleSignIn={socialSignIn} />

								<SignInApple appleSignIn={socialSignIn} />
							</div>
						</div>
					</div>
				</div>

				<div className="pass-links">
					<p className="no-account-tagline">{`Don't have a VIZIO Account?`}</p>
					<Link
						className="link-new-account link"
						to={{ pathname: '/sign-up', search: `?${queryStrings.toString()}` }}>
						Create one here
					</Link>
				</div>

				{isSpinnerLoading && <Spinner />}
			</div>
			<div className="icon-section">
				<ul className="icon-block">
					<li className="icon-item">
						<img
							src={iconvizio}
							className="icon-img"
							alt="Convenient account management through the VIZIO Mobile App"
						/>
						<p className="icon-cnt">Convenient account management through the VIZIO Mobile App</p>
					</li>
					<li className="icon-item">
						<img src={icontick} className="icon-img" alt="Automatic TV registration" />
						<p className="icon-cnt">Automatic TV registration</p>
					</li>
					<li className="icon-item">
						<img src={iconstar} className="icon-img" alt="Access to special sales and offers" />
						<p className="icon-cnt">Access to special sales and offers</p>
					</li>
					<li className="icon-item">
						<img
							src={iconcalendar}
							className="icon-img"
							alt="Get notified about new features & products"
						/>
						<p className="icon-cnt">Get notified about new features & products</p>
					</li>
					<li className="icon-item">
						<img
							src={iconwatsup}
							className="icon-img"
							alt="Easier tech support check in with  TV registration"
						/>
						<p className="icon-cnt">Easier tech support check in with TV registration</p>
					</li>
					<li className="icon-item">
						<img
							src={iconmultipletv}
							className="icon-img"
							alt="Manage multiple TVs with a single login"
						/>
						<p className="icon-cnt">Manage multiple TVs with a single login</p>
					</li>
				</ul>
			</div>
		</>
	);
};

export default SignIn;
